import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, CheckCircle, XCircle, Link as LinkIcon, Unlink, X } from 'lucide-react';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useUpgradeFlow } from '../../hooks/useUpgradeFlow';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useWebSocket } from '../../contexts/WebSocketContext';

interface MYOBStatus {
  connected: boolean;
  companyFileName?: string;
  lastSyncAt?: string;
  expiresAt?: string;
  handlePayroll?: boolean;
  bankFeedEnabled?: boolean;
  handleLeave?: boolean;
  syncedData?: {
    contacts?: number;
    customers?: number;
    suppliers?: number;
    personal?: number;
    staff?: number;
    staffActive?: number;
    staffHistorical?: number;
    taxRates?: number;
    chartOfAccounts?: number;
  };
}

export const MYOBIntegration: React.FC = () => {
  const { t } = useTranslation();
  const { openUpgradeFlow } = useUpgradeFlow();
  const { notifications } = useWebSocket();
  const [status, setStatus] = useState<MYOBStatus>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const processedNotifications = useRef<Set<string>>(new Set());

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchStatus();
  }, []);

  // Listen for completed sync notifications and refresh status
  useEffect(() => {
    notifications.forEach((notification) => {
      if (
        notification.type === 'myob_sync' &&
        notification.status === 'completed' &&
        !processedNotifications.current.has(notification.id)
      ) {
        processedNotifications.current.add(notification.id);
        setTimeout(() => {
          fetchStatus();
        }, 500);
      }
    });
  }, [notifications]);

  // Refresh status when detail modal is opened
  useEffect(() => {
    if (showDetailModal) {
      fetchStatus();
    }
  }, [showDetailModal]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/myob/sync-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus(data.data);
      } else {
        setError(data.message || 'Failed to fetch MYOB status');
      }
    } catch (err) {
      console.error('Fetch MYOB status error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/myob/connect`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // Open MYOB authorization in popup
        const width = 800;
        const height = 900;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          data.data.authorizationUrl,
          'MYOBAuth',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
        );

        // Poll for popup closure or message
        const pollTimer = setInterval(() => {
          if (popup && popup.closed) {
            clearInterval(pollTimer);
            setLoading(false);
            fetchStatus();
          }
        }, 500);

        // Listen for messages from the popup
        const messageHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'MYOB_AUTH_SUCCESS') {
            clearInterval(pollTimer);
            if (popup) popup.close();
            setSuccess('MYOB connected successfully! Your data is syncing in the background.');
            fetchStatus();
            window.removeEventListener('message', messageHandler);
          } else if (event.data.type === 'MYOB_LIMIT_ERROR') {
            clearInterval(pollTimer);
            if (popup) popup.close();
            setLoading(false);
            window.removeEventListener('message', messageHandler);
            openUpgradeFlow({
              limitType: event.data.limit?.type || 'integrations',
              limitDetails: event.data.limit
            });
          } else if (event.data.type === 'MYOB_AUTH_ERROR') {
            clearInterval(pollTimer);
            if (popup) popup.close();
            setError(event.data.message || 'Failed to connect MYOB');
            setLoading(false);
            window.removeEventListener('message', messageHandler);
          }
        };

        window.addEventListener('message', messageHandler);
      } else {
        setError(data.message || 'Failed to initiate MYOB connection');
        setLoading(false);
      }
    } catch (err) {
      console.error('Connect MYOB error:', err);
      setError('Failed to connect to MYOB');
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setError(null);
      setLoading(true);
      setShowDisconnectConfirm(false);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/myob/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('MYOB disconnected successfully');
        setStatus({ connected: false });
      } else {
        setError(data.message || 'Failed to disconnect MYOB');
      }
    } catch (err) {
      console.error('Disconnect MYOB error:', err);
      setError('Failed to disconnect from MYOB');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setError(null);
      setSyncing(true);

      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/myob/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('MYOB sync started - check notifications for progress');
        await fetchStatus();
        
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        setError(data.message || 'Failed to start sync');
      }
    } catch (err) {
      console.error('Sync MYOB error:', err);
      setError('Failed to start MYOB sync');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatTimeAgo = (dateString?: string) => {
    if (!dateString) return 'Never';
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return formatDate(dateString);
  };

  const handleUpdateSettings = async (setting: 'handlePayroll' | 'bankFeedEnabled' | 'handleLeave', value: boolean) => {
    try {
      setUpdatingSettings(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/myob/settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [setting]: value })
      });

      const data = await response.json();

      if (data.success) {
        setStatus(prev => ({ ...prev, [setting]: value }));
        setSuccess('Settings updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('Update settings error:', err);
      setError('Failed to update settings');
    } finally {
      setUpdatingSettings(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        isOpen={showDisconnectConfirm}
        title={t('admin.disconnectMYOB')}
        message={t('admin.disconnectMYOBMessage')}
        confirmLabel={t('admin.disconnect')}
        cancelLabel={t('common.cancel')}
        type="danger"
        onConfirm={handleDisconnect}
        onCancel={() => setShowDisconnectConfirm(false)}
      />

      {/* Compact Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6 h-full flex flex-col">
        <div className="mb-4">
          <div className="flex items-start mb-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 transition-colors ${
              status.connected 
                ? 'bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50' 
                : 'bg-gray-100 dark:bg-gray-800'
            }`}>
              <div className="w-8 h-8 bg-accent-600 rounded flex items-center justify-center text-white font-bold text-sm">
                MYOB
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                MYOB Integration
              </h3>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                  {t('admin.myobDescription')}
                </p>
                {status.connected ? (
                  <div className="flex items-center text-success-600 dark:text-success-400 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 mr-1.5" />
                    <span className="font-medium text-sm">{t('admin.connected')}</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-500 dark:text-gray-400 flex-shrink-0">
                    <XCircle className="w-4 h-4 mr-1.5" />
                    <span className="font-medium text-sm">{t('admin.notConnected')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-error-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-800 dark:text-success-400">{success}</p>
          </div>
        )}

        <div className="mt-auto pt-4">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
            </div>
          ) : status.connected ? (
            <div className="flex gap-2">
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-error-500 text-white rounded-lg hover:bg-error-600 transition-colors font-medium text-sm"
              >
                {t('admin.disconnect')}
              </button>
              <button
                onClick={() => setShowDetailModal(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-700 dark:text-gray-300 font-medium text-sm"
              >
                {t('admin.info')}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleConnect}
                disabled={loading}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                {t('admin.connect')}
              </button>
              <button
                onClick={() => setShowDetailModal(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-700 dark:text-gray-300 font-medium text-sm"
              >
                {t('admin.info')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.myobDetails')}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {status.connected ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.companyFile')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {status.companyFileName || 'Unknown'}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.lastSynced')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatDate(status.lastSyncAt)}
                      </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('admin.tokenExpires')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatDate(status.expiresAt)}
                      </p>
                    </div>
                  </div>

                  {/* Integration Settings */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('admin.integrationSettings')}</h3>
                
                {/* Handle Payroll Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('admin.myobHandlePayroll')}
                    </label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {t('admin.myobHandlePayrollDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleUpdateSettings('handlePayroll', !status.handlePayroll)}
                    disabled={updatingSettings}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      status.handlePayroll ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        status.handlePayroll ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Bank Feed Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {t('admin.bankFeedEnabled')}
                    </label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {t('admin.myobBankFeedDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleUpdateSettings('bankFeedEnabled', !status.bankFeedEnabled)}
                    disabled={updatingSettings}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      status.bankFeedEnabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        status.bankFeedEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Handle Leave Toggle */}
                {status.handlePayroll && (
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {t('admin.handleLeave')}
                      </label>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {t('admin.myobHandleLeaveDescription')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleUpdateSettings('handleLeave', !status.handleLeave)}
                      disabled={updatingSettings}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        status.handleLeave ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          status.handleLeave ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}
              </div>

              {status.syncedData && (
                <div className="bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg p-4">
                  <p className="text-sm font-semibold text-accent-900 dark:text-accent-100 mb-3">
                    {t('admin.syncedData')} from MYOB:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {typeof status.syncedData.contacts === 'number' && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                          {status.syncedData.contacts}
                        </p>
                        <p className="text-xs text-accent-700 dark:text-accent-300">{t('admin.myobContacts')}</p>
                      </div>
                    )}
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.syncedData.customers ?? 0}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">{t('admin.myobCustomers')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.syncedData.suppliers ?? 0}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">{t('admin.myobSuppliers')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.syncedData.personal ?? 0}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">{t('admin.myobPersonal')}</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${
                        (status.syncedData.staff ?? 0) > 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-primary-600 dark:text-primary-400'
                      }`}>
                        {status.syncedData.staff ?? 0}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">{t('admin.myobStaff')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.syncedData.taxRates ?? 0}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">{t('admin.myobTaxRates')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                        {status.syncedData.chartOfAccounts ?? 0}
                      </p>
                      <p className="text-xs text-accent-700 dark:text-accent-300">{t('admin.myobAccounts')}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex gap-3">
                  <button
                    onClick={handleSync}
                    disabled={syncing}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? t('admin.syncing') : t('admin.syncNow')}
                  </button>

                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowDisconnectConfirm(true);
                    }}
                    disabled={loading}
                    className="flex items-center justify-center px-4 py-2 bg-error-500 text-white rounded-lg hover:bg-error-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    {t('admin.disconnect')}
                  </button>
                </div>
              </div>

              <div className="p-4 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg">
                <div className="text-sm text-accent-800 dark:text-accent-400">
                  <p className="mb-1">
                    <strong>{t('admin.automaticSync')}:</strong> {t('admin.myobAutomaticSyncDescription')}
                  </p>
                  {status.lastSyncAt && (
                    <p className="text-xs text-accent-600 dark:text-accent-300">
                      {t('admin.lastSynced')}: {formatTimeAgo(status.lastSyncAt)}
                    </p>
                  )}
                </div>
              </div>
                </>
              ) : (
                <>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.myobNoIntegration')}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('admin.myobNoIntegrationHint')}
                    </p>
                  </div>
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        handleConnect();
                      }}
                      disabled={loading}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      <LinkIcon className="w-4 h-4" />
                      {t('admin.connect')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

